import { useState, useEffect, createContext, useContext, ReactNode } from "react";
import { X, Plus, ArrowRight, Scale, ShoppingCart, Check } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useCart } from "@/hooks/useCart";

interface Product {
  id: string;
  name: string;
  slug: string;
  price: number;
  original_price: number | null;
  images: string[] | null;
  description: string | null;
  specifications: Record<string, string> | null;
  rating: number | null;
  review_count: number | null;
  in_stock: boolean | null;
  materials: string[] | null;
  colors: string[] | null;
}

const STORAGE_KEY = "product_comparison";
const MAX_COMPARE = 4;

// Context for global state
interface ComparisonContextType {
  compareIds: string[];
  addToCompare: (productId: string) => void;
  removeFromCompare: (productId: string) => void;
  clearComparison: () => void;
  isInCompare: (productId: string) => boolean;
}

const ComparisonContext = createContext<ComparisonContextType | undefined>(undefined);

export function ComparisonProvider({ children }: { children: ReactNode }) {
  const [compareIds, setCompareIds] = useState<string[]>([]);
  const { toast } = useToast();

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        setCompareIds(JSON.parse(stored));
      } catch {
        localStorage.removeItem(STORAGE_KEY);
      }
    }
   
  }, []);

  const addToCompare = (productId: string) => {
    if (compareIds.includes(productId)) {
      toast({ title: "Already in comparison", description: "This product is already being compared" });
      return;
    }
    if (compareIds.length >= MAX_COMPARE) {
      toast({ title: "Limit reached", description: `You can compare up to ${MAX_COMPARE} products`, variant: "destructive" });
      return;
    }
    const newIds = [...compareIds, productId];
    setCompareIds(newIds);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(newIds));
    toast({ title: "Added to comparison", description: "View the comparison bar at the bottom" });
  };

  const removeFromCompare = (productId: string) => {
    const newIds = compareIds.filter(id => id !== productId);
    setCompareIds(newIds);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(newIds));
  };

  const clearComparison = () => {
    setCompareIds([]);
    localStorage.removeItem(STORAGE_KEY);
  };

  const isInCompare = (productId: string) => compareIds.includes(productId);

  return (
    <ComparisonContext.Provider value={{ compareIds, addToCompare, removeFromCompare, clearComparison, isInCompare }}>
      {children}
    </ComparisonContext.Provider>
  );
}

export function useProductComparison() {
  const context = useContext(ComparisonContext);
  if (context === undefined) {
    throw new Error("useProductComparison must be used within a ComparisonProvider");
  }
  return context;
}

const formatPrice = (price: number) => {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(price);
};

export function ComparisonBar() {
  const { compareIds, removeFromCompare, clearComparison } = useProductComparison();
  const [products, setProducts] = useState<Product[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    if (compareIds.length > 0) {
      fetchProducts();
    } else {
      setProducts([]);
    }
    // Auto-close dialog if less than 2 products remain
    if (compareIds.length < 2 && isOpen) {
      setIsOpen(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compareIds, isOpen]);

  const fetchProducts = async () => {
    const { data } = await supabase
      .from("products")
      .select("*")
      .in("id", compareIds);
    if (data) {
      // Maintain the order of compareIds
      const orderedProducts = compareIds
        .map(id => data.find(p => p.id === id))
        .filter(Boolean) as Product[];
      setProducts(orderedProducts);
    }
  };

  if (compareIds.length === 0) return null;

  return (
    <>
      {/* Floating "Show" button when bar is hidden */}
      {!isVisible && (
        <button
          onClick={() => setIsVisible(true)}
          className="fixed bottom-6 left-6 z-40 flex items-center gap-2 px-4 py-2 bg-gold text-white rounded-full shadow-lg hover:bg-gold/90 transition-colors animate-in fade-in slide-in-from-left-4 duration-300"
        >
          <Scale size={16} />
          <span className="text-sm font-medium">Compare ({products.length})</span>
        </button>
      )}

      {/* Floating Bar */}
      <div 
        className={`fixed bottom-0 left-0 right-0 bg-card/95 backdrop-blur-sm border-t border-border p-4 z-40 shadow-2xl transition-transform duration-300 ${
          isVisible ? "translate-y-0" : "translate-y-full"
        }`}
      >
        <div className="container-luxury">
          {/* Header with toggle */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Scale size={18} className="text-primary" />
              <span className="font-display text-sm">Compare Products</span>
              <Badge variant="secondary" className="text-xs">
                {products.length}/{MAX_COMPARE}
              </Badge>
            </div>
            <button 
              onClick={() => setIsVisible(false)}
              className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
            >
              <X size={14} />
              Hide
            </button>
          </div>

          <div className="flex items-center justify-between gap-4">
            {/* Products */}
            <div className="flex items-center gap-3 overflow-x-auto flex-1 pb-1">
              {products.map((product) => (
                <div 
                  key={product.id} 
                  className="relative flex-shrink-0 group animate-in fade-in slide-in-from-bottom-2 duration-300"
                >
                  <div className="relative">
                    <img
                      src={product.images?.[0] || "/placeholder.svg"}
                      alt={product.name}
                      className="w-16 h-16 object-cover border border-border rounded-sm"
                    />
                    <button
                      onClick={() => removeFromCompare(product.id)}
                      className="absolute -top-2 -right-2 w-5 h-5 bg-destructive text-destructive-foreground rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-sm"
                    >
                      <X size={12} />
                    </button>
                  </div>
                  <p className="text-xs text-center mt-1 max-w-16 truncate">{product.name}</p>
                </div>
              ))}
              {/* Empty slots */}
              {Array.from({ length: MAX_COMPARE - products.length }).map((_, i) => (
                <div 
                  key={`empty-${i}`} 
                  className="w-16 h-16 border-2 border-dashed border-muted rounded-sm flex items-center justify-center flex-shrink-0"
                >
                  <Plus size={16} className="text-muted-foreground" />
                </div>
              ))}
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2 flex-shrink-0">
              <Button variant="ghost" size="sm" onClick={clearComparison}>
                Clear
              </Button>
              <Dialog open={isOpen} onOpenChange={setIsOpen}>
                <DialogTrigger asChild>
                  <Button disabled={products.length < 2} className="gap-2">
                    Compare
                    <ArrowRight size={16} />
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                      <Scale size={20} />
                      Product Comparison
                    </DialogTitle>
                  </DialogHeader>
                  <ComparisonTable products={products} onRemove={removeFromCompare} onClose={() => setIsOpen(false)} />
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function ComparisonTable({ products, onRemove, onClose }: { products: Product[]; onRemove: (id: string) => void; onClose: () => void }) {
  const { addToCart } = useCart();
  const { toast } = useToast();
  
  const allSpecs = new Set<string>();
  products.forEach(p => {
    if (p.specifications) {
      Object.keys(p.specifications).forEach(key => allSpecs.add(key));
    }
  });

  const handleAddToCart = async (productId: string, productName: string) => {
    await addToCart(productId, 1);
    toast({ title: "Added to cart", description: `${productName} added to your cart` });
  };

  // Find best values for highlighting
  const lowestPrice = Math.min(...products.map(p => p.price));
  const highestRating = Math.max(...products.filter(p => p.rating).map(p => p.rating || 0));

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse">
        <thead>
          <tr>
            <th className="p-4 text-left border-b border-border w-36 bg-muted/30 sticky left-0"></th>
            {products.map((product) => (
              <th key={product.id} className="p-4 border-b border-border min-w-[220px]">
                <div className="relative">
                  <button
                    onClick={() => onRemove(product.id)}
                    className="absolute -top-2 right-0 w-6 h-6 bg-muted rounded-full flex items-center justify-center hover:bg-destructive hover:text-destructive-foreground transition-colors"
                  >
                    <X size={14} />
                  </button>
                  <img
                    src={product.images?.[0] || "/placeholder.svg"}
                    alt={product.name}
                    className="w-full h-52 object-contain bg-gray-50 mb-3 rounded"
                  />
                  <Link 
                    to={`/product/${product.slug}`} 
                    className="font-display text-sm hover:text-primary transition-colors line-clamp-2"
                  >
                    {product.name}
                  </Link>
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {/* Price Row */}
          <tr className="bg-muted/20">
            <td className="p-4 border-b border-border font-medium sticky left-0 bg-muted/30">Price</td>
            {products.map((product) => (
              <td key={product.id} className="p-4 border-b border-border">
                <div className="flex items-center gap-2">
                  <span className={`text-lg font-semibold ${product.price === lowestPrice ? "text-green-600" : ""}`}>
                    {formatPrice(product.price)}
                  </span>
                  {product.price === lowestPrice && products.length > 1 && (
                    <Badge variant="secondary" className="bg-green-100 text-green-700 text-xs">
                      Best
                    </Badge>
                  )}
                </div>
                {product.original_price && (
                  <span className="text-sm text-muted-foreground line-through">
                    {formatPrice(product.original_price)}
                  </span>
                )}
              </td>
            ))}
          </tr>
          
          {/* Rating Row */}
          <tr>
            <td className="p-4 border-b border-border font-medium sticky left-0 bg-background">Rating</td>
            {products.map((product) => (
              <td key={product.id} className="p-4 border-b border-border">
                {product.rating ? (
                  <div className="flex items-center gap-2">
                    <span className={product.rating === highestRating ? "text-amber-600 font-medium" : ""}>
                      {product.rating.toFixed(1)} ⭐
                    </span>
                    <span className="text-sm text-muted-foreground">
                      ({product.review_count} reviews)
                    </span>
                    {product.rating === highestRating && products.length > 1 && (
                      <Badge variant="secondary" className="bg-amber-100 text-amber-700 text-xs">
                        Top Rated
                      </Badge>
                    )}
                  </div>
                ) : (
                  <span className="text-muted-foreground">No reviews</span>
                )}
              </td>
            ))}
          </tr>
          
          {/* Availability Row */}
          <tr className="bg-muted/20">
            <td className="p-4 border-b border-border font-medium sticky left-0 bg-muted/30">Availability</td>
            {products.map((product) => (
              <td key={product.id} className="p-4 border-b border-border">
                {product.in_stock ? (
                  <div className="flex items-center gap-1 text-green-600">
                    <Check size={16} />
                    <span>In Stock</span>
                  </div>
                ) : (
                  <span className="text-destructive">Out of Stock</span>
                )}
              </td>
            ))}
          </tr>
          
          {/* Materials Row */}
          <tr>
            <td className="p-4 border-b border-border font-medium sticky left-0 bg-background">Materials</td>
            {products.map((product) => (
              <td key={product.id} className="p-4 border-b border-border">
                {product.materials?.length ? (
                  <div className="flex flex-wrap gap-1">
                    {product.materials.map((m, i) => (
                      <Badge key={i} variant="outline" className="text-xs">{m}</Badge>
                    ))}
                  </div>
                ) : "-"}
              </td>
            ))}
          </tr>
          
          {/* Colors Row */}
          <tr className="bg-muted/20">
            <td className="p-4 border-b border-border font-medium sticky left-0 bg-muted/30">Colors</td>
            {products.map((product) => (
              <td key={product.id} className="p-4 border-b border-border">
                {product.colors?.join(", ") || "-"}
              </td>
            ))}
          </tr>
          
          {/* Dynamic Specifications */}
          {Array.from(allSpecs).map((spec, index) => (
            <tr key={spec} className={index % 2 === 0 ? "" : "bg-muted/20"}>
              <td className={`p-4 border-b border-border font-medium capitalize sticky left-0 ${index % 2 === 0 ? "bg-background" : "bg-muted/30"}`}>
                {spec.replace(/([A-Z])/g, ' $1').trim()}
              </td>
              {products.map((product) => (
                <td key={product.id} className="p-4 border-b border-border">
                  {product.specifications?.[spec] || "-"}
                </td>
              ))}
            </tr>
          ))}
          
          {/* Description Row */}
          <tr>
            <td className="p-4 border-b border-border font-medium sticky left-0 bg-background">Description</td>
            {products.map((product) => (
              <td key={product.id} className="p-4 border-b border-border text-sm text-muted-foreground">
                {product.description?.slice(0, 120)}
                {product.description && product.description.length > 120 ? "..." : ""}
              </td>
            ))}
          </tr>
          
          {/* Actions Row */}
          <tr>
            <td className="p-4 font-medium sticky left-0 bg-background">Action</td>
            {products.map((product) => (
              <td key={product.id} className="p-4">
                <div className="flex flex-col gap-2">
                  <Button 
                    onClick={() => handleAddToCart(product.id, product.name)}
                    disabled={!product.in_stock}
                    className="w-full"
                    size="sm"
                  >
                    <ShoppingCart size={16} className="mr-2" />
                    Add to Cart
                  </Button>
                  <Button variant="outline" size="sm" asChild className="w-full" onClick={onClose}>
                    <Link to={`/product/${product.slug}`}>View Details</Link>
                  </Button>
                </div>
              </td>
            ))}
          </tr>
        </tbody>
      </table>
    </div>
  );
}
